/**
 * Onyx's DEMO catalog — the BERQ range, and what a grid falls back to when nothing real resolves.
 *
 * It lives in ui-react, not in the storefront app, because the BUILDER needs it too: a seller opening
 * Onyx on an empty store would otherwise get the house fallback (a clothing shop, "Cotton Crew Tee")
 * inside an electronics theme (spec 0032 §3 always listed `catalog` as one of a theme's four pieces).
 *
 * TYPE-ONLY IMPORTS on purpose: `sections/_shared/product.ts` reads this module through
 * `themes/demoCatalogs.ts`, so anything but a type import here would close an import cycle.
 *
 * Prices carry the STORE's currency rather than one of their own — the same demo has to read correctly
 * in a MAD shop and in a EUR one (0049).
 *
 * THE OPTION IS `Coloris`, and that is not decoration: `collection-grid` ships default facets named
 * "Color" and "Size", which in an electronics shop are two dead menus at the top of the page (playbook
 * trap 20). The grid points its swatches at this option name instead.
 */
const IMG = '/demo/tech';
function product(currency, p) {
    return {
        id: p.id,
        name: p.name,
        slug: p.slug,
        description: p.desc,
        price: { amount: p.price, currency },
        compareAtPrice: p.compareAt ? { amount: p.compareAt, currency } : null,
        images: [
            { url: `${IMG}/${p.image}`, alt: p.alt, isCover: true },
            ...(p.gallery ?? []).map((g) => ({ url: `${IMG}/${g.file}`, alt: g.alt, isCover: false })),
        ],
        inventory: { tracked: false, quantity: null },
        seo: { metaTitle: null, metaDescription: null },
        status: 'active',
        tags: p.badge ? [p.badge] : [],
        vendor: 'BERQ',
        rating: p.rating,
        options: [
            {
                name: 'Coloris',
                values: (p.colors ?? ['Noir mat', 'Graphite']).map((label) => ({ label })),
            },
        ],
    };
}
/** The BERQ range, in merchandising order — the flagship first, the accessories last. */
export function onyxDemoProducts(currency) {
    return [
        product(currency, {
            id: 'p-berq-one',
            name: 'BERQ ONE — Casque à réduction de bruit',
            slug: 'berq-one-casque-reduction-de-bruit',
            price: 249000,
            compareAt: 299000,
            badge: 'custom-badge-1',
            image: 'product-one.webp',
            alt: 'Casque sans fil BERQ ONE, aluminium noir mat',
            rating: { value: 4.8, count: 612 },
            desc: 'Huit microphones, 42 dB de réduction active, 40 heures d’autonomie. Le silence, puis la musique.',
            // Le vaisseau amiral porte SA galerie. Sans elle, l'aperçu résolvait le produit par son slug
            // dans CE catalogue — pas `ONYX_DEMO_PRODUCT` — et la fiche du ONE montrait cinq fois la même
            // photo. Le vendeur qui ouvre le thème sur une boutique vide voit la même chose.
            gallery: [
                { file: 'one-profile.webp', alt: 'Le casque BERQ ONE de profil' },
                { file: 'one-flat.webp', alt: 'Le casque BERQ ONE replié à plat' },
                {
                    file: 'tech-materials.webp',
                    alt: 'Macro du coussinet en mousse à mémoire et de l’arceau en aluminium',
                },
                { file: 'one-worn.webp', alt: 'Le casque BERQ ONE porté, de profil' },
            ],
        }),
        product(currency, {
            id: 'p-berq-air-pro',
            name: 'BERQ AIR PRO — Écouteurs sans fil',
            slug: 'berq-air-pro-ecouteurs-sans-fil',
            price: 129000,
            image: 'product-air-pro.webp',
            alt: 'Écouteurs BERQ AIR PRO et leur boîtier en aluminium',
            rating: { value: 4.7, count: 431 },
            desc: 'Réduction adaptative, transparence naturelle, boîtier aluminium. 8 h dans l’oreille, 32 h au total.',
        }),
        product(currency, {
            id: 'p-berq-air',
            name: 'BERQ AIR — Écouteurs sans fil',
            slug: 'berq-air-ecouteurs-sans-fil',
            price: 89000,
            compareAt: 99000,
            badge: 'custom-badge-2',
            image: 'product-air.webp',
            alt: 'Écouteurs BERQ AIR et leur boîtier noir mat',
            rating: { value: 4.6, count: 388 },
            desc: 'La même signature sonore, sans la réduction adaptative. 30 heures avec le boîtier.',
        }),
        product(currency, {
            id: 'p-berq-pulse',
            name: 'BERQ PULSE — Montre connectée',
            slug: 'berq-pulse-montre-connectee',
            price: 119000,
            image: 'product-pulse.webp',
            alt: 'Montre connectée BERQ PULSE, boîtier aluminium noir',
            rating: { value: 4.6, count: 254 },
            desc: 'Écran AMOLED lisible en plein soleil, 14 jours d’autonomie, suivi du sommeil et du rythme cardiaque.',
        }),
        product(currency, {
            id: 'p-berq-beam',
            name: 'BERQ BEAM — Enceinte portable',
            slug: 'berq-beam-enceinte-portable',
            price: 79000,
            image: 'product-beam.webp',
            alt: 'Enceinte portable BERQ BEAM, grille technique noire',
            rating: { value: 4.7, count: 197 },
            desc: '360°, 20 W, étanche IPX7. Deux enceintes s’apparient en stéréo d’une pression.',
        }),
        product(currency, {
            id: 'p-berq-core',
            name: 'BERQ CORE — Batterie magnétique 10 000 mAh',
            slug: 'berq-core-batterie-magnetique',
            price: 49000,
            image: 'product-core.webp',
            alt: 'Batterie externe magnétique BERQ CORE',
            rating: { value: 4.5, count: 312 },
            desc: 'Se colle au dos du téléphone, recharge en 15 W sans fil et en 30 W par le câble.',
        }),
        product(currency, {
            id: 'p-berq-watt',
            name: 'BERQ WATT — Chargeur GaN 65 W',
            slug: 'berq-watt-chargeur-gan-65w',
            price: 39000,
            image: 'product-watt.webp',
            alt: 'Chargeur mural GaN BERQ WATT, format compact',
            rating: { value: 4.8, count: 276 },
            desc: 'Trois ports, 65 W répartis automatiquement. Assez petit pour rester dans la poche du sac.',
        }),
        product(currency, {
            id: 'p-berq-link',
            name: 'BERQ LINK — Câble tressé USB-C 100 W',
            slug: 'berq-link-cable-tresse-usb-c',
            price: 14900,
            image: 'product-link.webp',
            alt: 'Câble USB-C tressé BERQ LINK, deux mètres',
            rating: { value: 4.7, count: 508 },
            desc: 'Deux mètres, gaine tressée, 100 W et 480 Mb/s. Testé à 25 000 pliages.',
            colors: ['Graphite', 'Noir mat'],
        }),
    ];
}
/**
 * Onyx merchandises by USAGE, not by product family — c'est ainsi qu'on entre dans un magasin
 * d'électronique : on vient « pour écouter », « pour tenir la journée », pas « pour un transducteur ».
 *
 * QUATRE collections, pas cinq : `collection-list` plafonne à 4 colonnes et une cinquième tuile
 * laisserait une rangée bancale (piège n°3 du mode d'emploi, la colonne fantôme).
 */
export function onyxDemoCollections() {
    const make = (id, name, slug, cover, alt, productCount, description) => ({
        id,
        name,
        slug,
        description,
        coverImage: { url: `${IMG}/${cover}`, alt },
        productCount,
    });
    return [
        make('c-audio', 'Audio', 'audio', 'cat-audio.webp', 'Casque et écouteurs BERQ posés sur une surface sombre', 3, 'Casques et écouteurs. Le silence d’abord, la musique ensuite.'),
        make('c-montres', 'Montres', 'montres', 'cat-montres.webp', 'Montre connectée BERQ portée au poignet', 1, 'Quatorze jours d’autonomie, et un écran qu’on lit dehors.'),
        make('c-energie', 'Énergie', 'energie', 'cat-energie.webp', 'Batterie externe et chargeur BERQ sur une surface sombre', 2, 'Chargeurs et batteries. Assez petits pour être toujours là.'),
        make('c-accessoires', 'Accessoires', 'accessoires', 'cat-accessoires.webp', 'Câble tressé, enceinte et boîtier d’écouteurs BERQ', 2, 'Câbles, enceintes et ce qui va avec, testés aussi longtemps que le reste.'),
    ];
}
//# sourceMappingURL=catalog.js.map